// src/scenes/team/index.jsx

import React, { useState, useEffect } from "react";
import axios from "axios";
import {
    Box,
    Typography,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useTheme } from "@mui/material/styles";
import bcrypt from "bcryptjs";
import Header from "../../components/Header";

const API_USERS = "https://figazuresql.swancreate.com/api/swa/UserMst";
const API_ROLES = "https://figazuresql.swancreate.com/api/swa/RoleMst";
const API_SCHOOLS = "https://figazuresql.swancreate.com/api/swa/SchoolMst";
const API_ALLOCATIONS = "https://figazuresql.swancreate.com/api/swa/SchoolAllocation";

export default function Team() {
    const theme = useTheme();
    const [team, setTeam] = useState([]);
    const [roles, setRoles] = useState([]);
    const [schools, setSchools] = useState([]);
    const [loading, setLoading] = useState(false);

    // editing state
    const [editingApiId, setEditing] = useState(null);
    const [updated, setUpdated] = useState({
        apiid: "",
        UserId: "",
        FirstName: "",
        LastName: "",
        Email: "",
        RoleId: "",
        allocations: [],  // { apiid, SchoolId }
    });

    // add-user dialog
    const [openAdd, setOpenAdd] = useState(false);
    const [newUser, setNewUser] = useState({
        FirstName: "",
        LastName: "",
        Email: "",
        RoleId: "",
        Password: "",
    });

    useEffect(() => {
        loadAll();
    }, []);

    async function loadAll() {
        setLoading(true);
        try {
            const [uRes, rRes, sRes, aRes] = await Promise.all([
                axios.get(API_USERS),
                axios.get(API_ROLES),
                axios.get(API_SCHOOLS),
                axios.get(API_ALLOCATIONS),
            ]);

            setRoles(rRes.data);
            setSchools(sRes.data);

            // group allocations by the user’s *UserId* (allocation.userid)
            const byUser = {};
            for (const a of aRes.data) {
                if (!a.userid) continue;
                byUser[a.userid] = byUser[a.userid] || [];
                byUser[a.userid].push(a);
            }

            // merge allocations into users
            const rows = uRes.data.map((u) => ({
                ...u,
                allocations: byUser[u.UserId] || [],
            }));
            setTeam(rows);
        } catch (e) {
            console.error("loadAll error:", e);
        }
        setLoading(false);
    }

    // — Add User Handlers —
    const handleAddOpen = () => setOpenAdd(true);
    const handleAddClose = () => {
        setOpenAdd(false);
        setNewUser({ FirstName: "", LastName: "", Email: "", RoleId: "", Password: "" });
    };
    const handleNewChange = (field, value) =>
        setNewUser((u) => ({ ...u, [field]: value }));

    const handleAddSubmit = async () => {
        setLoading(true);
        try {
            const hashed = bcrypt.hashSync(newUser.Password, 10);
            await axios.post(API_USERS, {
                FirstName: newUser.FirstName,
                LastName: newUser.LastName,
                Email: newUser.Email,
                RoleId: newUser.RoleId,
                Password: hashed,
            });
            await loadAll();
            handleAddClose();
        } catch (err) {
            console.error("handleAddSubmit error:", err);
        }
        setLoading(false);
    };

    // — Start Editing —
    const startEdit = (apiid) => {
        const u = team.find((r) => r.apiid === apiid);
        setEditedUser(u);
        setEditing(apiid);
    };

    const setEditedUser = (u) => {
        setUpdated({
            apiid: u.apiid,
            UserId: u.UserId,
            FirstName: u.FirstName,
            LastName: u.LastName,
            Email: u.Email,
            RoleId: u.RoleId,
            allocations: u.allocations.map((a) => ({
                apiid: a.apiid,
                SchoolId: a.SchoolId,
            })),
        });
    };

    const handleChange = (field, value) =>
        setUpdated((u) => ({ ...u, [field]: value }));

    // — Save Edits: PUT UserMst/{apiid}, then delete & re-post allocations by UserId —
    const handleSave = async () => {
        setLoading(true);
        try {
            const { apiid, UserId, FirstName, LastName, Email, RoleId, allocations } = updated;

            // 1) update UserMst by apiid
            await axios.put(`${API_USERS}/${apiid}`, {
                FirstName,
                LastName,
                Email,
                RoleId,
            });

            // 2) delete old allocations (by allocation.apiid)
            const oldAllocs = team.find((r) => r.apiid === apiid).allocations || [];
            for (const a of oldAllocs) {
                await axios.delete(`${API_ALLOCATIONS}/${a.apiid}`);
            }

            // 3) post new allocations with userid = UserId
            for (const sid of allocations.map((a) => a.SchoolId)) {
                const sch = schools.find((s) => s.SchoolId === sid) || {};
                await axios.post(API_ALLOCATIONS, {
                    userid: UserId,
                    SchoolId: sid,
                    SchoolName: sch.SchoolName,
                    FirstName,
                    LastName,
                    email: Email,
                });
            }

            await loadAll();
            setEditing(null);
        } catch (e) {
            console.error("handleSave error:", e);
        }
        setLoading(false);
    };

    // — Grid Columns —
    const columns = [
        {
            field: "FirstName",
            headerName: "First Name",
            flex: 1,
            renderCell: (p) =>
                editingApiId === p.row.apiid ? (
                    <TextField
                        value={updated.FirstName}
                        onChange={(e) => handleChange("FirstName", e.target.value)}
                        size="small"
                        fullWidth
                    />
                ) : (
                    <Typography>{p.value}</Typography>
                ),
        },
        {
            field: "LastName",
            headerName: "Last Name",
            flex: 1,
            renderCell: (p) =>
                editingApiId === p.row.apiid ? (
                    <TextField
                        value={updated.LastName}
                        onChange={(e) => handleChange("LastName", e.target.value)}
                        size="small"
                        fullWidth
                    />
                ) : (
                    <Typography>{p.value}</Typography>
                ),
        },
        {
            field: "Email",
            headerName: "Email",
            flex: 1,
            renderCell: (p) =>
                editingApiId === p.row.apiid ? (
                    <TextField
                        value={updated.Email}
                        onChange={(e) => handleChange("Email", e.target.value)}
                        size="small"
                        fullWidth
                    />
                ) : (
                    <Typography>{p.value}</Typography>
                ),
        },
        {
            field: "RoleId",
            headerName: "Role",
            flex: 1,
            renderCell: (p) => {
                if (editingApiId === p.row.apiid) {
                    return (
                        <FormControl size="small" fullWidth>
                            <InputLabel>Role</InputLabel>
                            <Select
                                value={updated.RoleId}
                                label="Role"
                                onChange={(e) => handleChange("RoleId", e.target.value)}
                            >
                                {roles.map((r) => (
                                    <MenuItem key={r.RoleId} value={r.RoleId}>
                                        {r.RoleName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    );
                }
                const name = roles.find((r) => r.RoleId === p.value)?.RoleName || "—";
                return <Typography>{name}</Typography>;
            },
        },
        {
            field: "allocations",
            headerName: "Sites",
            flex: 2,
            renderCell: (p) => {
                const assigned =
                    editingApiId === p.row.apiid
                        ? updated.allocations.map((a) => a.SchoolId)
                        : p.row.allocations.map((a) => a.SchoolId);

                if (editingApiId === p.row.apiid) {
                    return (
                        <FormControl size="small" fullWidth>
                            <InputLabel>Sites</InputLabel>
                            <Select
                                multiple
                                value={assigned}
                                label="Sites"
                                renderValue={(sel) =>
                                    sel.map(
                                        (id) => schools.find((s) => s.SchoolId === id)?.SchoolName
                                    ).join(", ")
                                }
                                onChange={(e) => {
                                    const newIds = e.target.value;
                                    const oldMap = Object.fromEntries(
                                        updated.allocations.map((a) => [a.SchoolId, a.apiid])
                                    );
                                    setUpdated((u) => ({
                                        ...u,
                                        allocations: newIds.map((sid) => ({
                                            apiid: oldMap[sid] || null,
                                            SchoolId: sid,
                                        })),
                                    }));
                                }}
                            >
                                {schools.map((s) => (
                                    <MenuItem key={s.SchoolId} value={s.SchoolId}>
                                        <Checkbox checked={assigned.includes(s.SchoolId)} />
                                        <ListItemText primary={s.SchoolName} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    );
                }

                const names = assigned
                    .map((id) => schools.find((s) => s.SchoolId === id)?.SchoolName)
                    .join(", ");
                return <Typography>{names}</Typography>;
            },
        },
        {
            field: "actions",
            headerName: "Actions",
            flex: 1,
            renderCell: ({ row }) =>
                editingApiId === row.apiid ? (
                    <Button variant="contained" onClick={handleSave} sx={{ bgcolor: "green", color: "#fff" }}>
                        Save
                    </Button>
                ) : (
                    <Button variant="contained" onClick={() => startEdit(row.apiid)} sx={{ bgcolor: "green", color: "#fff" }}>
                        Edit
                    </Button>
                ),
        },
    ];

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box m={2} p={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Header title="TEAM" subtitle="Manage Team Members & Site Assignments" />
                <Button variant="contained" onClick={handleAddOpen}>Add User</Button>
            </Box>

            {/* Add User Dialog */}
            <Dialog open={openAdd} onClose={handleAddClose} fullWidth maxWidth="sm">
                <DialogTitle>Add New User</DialogTitle>
                <DialogContent>
                    <TextField
                        label="First Name" fullWidth margin="dense"
                        value={newUser.FirstName}
                        onChange={(e) => handleNewChange("FirstName", e.target.value)}
                    />
                    <TextField
                        label="Last Name" fullWidth margin="dense"
                        value={newUser.LastName}
                        onChange={(e) => handleNewChange("LastName", e.target.value)}
                    />
                    <TextField
                        label="Email" fullWidth margin="dense"
                        value={newUser.Email}
                        onChange={(e) => handleNewChange("Email", e.target.value)}
                    />
                    <FormControl fullWidth margin="dense" size="small">
                        <InputLabel>Role</InputLabel>
                        <Select
                            value={newUser.RoleId}
                            label="Role"
                            onChange={(e) => handleNewChange("RoleId", e.target.value)}
                        >
                            {roles.map((r) => (
                                <MenuItem key={r.RoleId} value={r.RoleId}>{r.RoleName}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Password" type="password" fullWidth margin="dense"
                        value={newUser.Password}
                        onChange={(e) => handleNewChange("Password", e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleAddClose}>Cancel</Button>
                    <Button variant="contained" onClick={handleAddSubmit}>Create</Button>
                </DialogActions>
            </Dialog>

            {/* DataGrid */}
            <Box height="75vh">
                <DataGrid
                    rows={team}
                    columns={columns}
                    getRowId={(r) => r.apiid}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    components={{ Toolbar: GridToolbar }}
                    disableSelectionOnClick
                    sx={{
                        "& .MuiDataGrid-row:nth-of-type(odd)": {
                            backgroundColor: theme.palette.action.hover,
                        },
                    }}
                />
            </Box>
        </Box>
    );
}
